"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, FileCheck2, Hammer, CircleCheck as CheckCircle2, ArrowRight, PhoneCall, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface ProcessStep {
  step: string;
  title: string;
  summary: string;
  details: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const steps: ProcessStep[] = [{
  step: "01",
  title: "Design & Planning",
  summary: "Evaluating architectural requirements and finish selections.",
  details: "We assess structural impacts, draft build blueprints, and specify exact materials and fixtures before work begins.",
  icon: Compass
}, {
  step: "02",
  title: "Permitting & Budgeting",
  summary: "Securing municipal building permits and aligning itemized estimates with insurance settlements.",
  details: "Our local managers file regional permits across MD, DC, and Northern VA while balancing line-item costs with adjusters.",
  icon: FileCheck2
}, {
  step: "03",
  title: "Construction & Rebuild",
  summary: "Structural framing, MEP trades, drywall, and custom installation.",
  details: "Certified TriServe carpenters and licensed trades execute rough-ins, drywall, carpentry, and electrical to code.",
  icon: Hammer
}, {
  step: "04",
  title: "Final Walkthrough & Handoff",
  summary: "Comprehensive punch-list completion, client sign-off, and warranty transfer.",
  details: "We inspect every detail alongside you, verify every trade standard, and issue comprehensive warranty documentation.",
  icon: CheckCircle2
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-process" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Top Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4 mb-10 sm:mb-14">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-primary" />
            <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
              Reconstruction Workflow &bull; Turnkey Protocol
            </span>
          </div>
          <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
            <ShieldCheck className="size-3.5 text-primary" />
            <span>MD &bull; DC &bull; Northern VA Direct Crew Control</span>
          </div>
        </div>

        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
            Structured Rebuild
          </p>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            How your rebuild comes together
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            We guide your property through every stage from initial architectural review and city permits to custom trades and structural inspection, keeping you and your insurance provider informed at each step.
          </p>
        </div>

        {/* 4-Step Process Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.step} data-index={index} variants={cardVariants} className="group relative flex flex-col justify-between bg-muted/60 hover:bg-card border border-border rounded-lg p-6 sm:p-7 transition-[background-color,border-color,box-shadow] duration-200 hover:border-primary hover:shadow-md">
                <div>
                  {/* Step Number & Icon Header */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                    <span className="font-mono text-2xl font-bold tracking-tight text-primary">
                      {item.step}
                    </span>
                    <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                      <Icon className="size-5" />
                    </div>
                  </div>

                  {/* Title & Phase Descriptions */}
                  <h3 className="font-default text-xl font-bold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm font-medium text-foreground/90 mb-3 leading-snug">
                    {item.summary}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.details}
                  </p>
                </div>

                {/* Sub-label for sequence validation */}
                <div className="pt-6 mt-6 border-t border-border flex items-center justify-between text-xs font-mono text-muted-foreground">
                  <span>Phase {index + 1} of 4</span>
                  <span className="text-primary font-semibold">TriServe Field Direct</span>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Operational Assurance & Immediate Contact Bar */}
        <div className="mt-12 sm:mt-16 rounded-lg bg-card border border-border p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xs">
          <div className="max-w-xl">
            <h4 className="font-default text-lg font-bold text-foreground mb-1">
              Ready to schedule an on-site structural evaluation?
            </h4>
            <p className="text-sm text-muted-foreground">
              Our regional teams respond directly without call center middle steps. Call our team anytime or submit your project details online.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestServicePage">
                <span>Request rebuild assessment</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border hover:bg-muted">
              <a href="tel:855-435-6631" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4 text-primary" />
                <span className="font-mono text-sm font-bold">855-435-6631</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}